import { datadogRum } from '@datadog/browser-rum';
import { kebabCase } from 'lodash';
import { scrollValidationErrorIntoCenterViewInScrollContainer } from '@frontend/utils';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import type { SupervisorsAssessmentBiometrics } from '../../../../common/enums/supervisorsAssessmentBiometrics';
import type { ValidationErrors } from '../../../utils/validation';
import type { IProcess } from '../../interfaces/process';
import type { IServiceGetOne } from '../../interfaces/serviceGetOne';
import type { ISupervisionGetOne } from '../../interfaces/supervisionGetOne';
import type { INewSupervision } from './SupervisionItem';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ReportCategories } from '../../../../common/enums/reportCategories';
import { getSupervisionValidationType } from '../../../../common/validation/getSupervisionValidationType';
import {
    getSaveAndSendValidationSchema,
    getSupervisionValidationSchema,
} from '../../../../common/validation/supervision';
import { fetchJson } from '../../../store/fetch';
import {
    emptyValidationErrors,
    updateValidationErrorsForOne,
    validate,
    validationErrorsToString,
} from '../../../utils/validation';
import { Component } from '../../elements/Component';
import { initializeCustomControl } from '../customControls/initializeCustomControl';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { validateCustomControlData } from '../customControls/validateCustomControlData';
import { validateOneCustomControlData } from '../customControls/validateOneCustomControlData';

interface ISupervisionItemEditProps {
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    onCancel: () => Promise<void>;
    onSave: (id: string) => Promise<void>;
    process: IProcess;
    supervision: ISupervisionGetOne | INewSupervision;
}

export interface ISupervisionEdit {
    assessmentBiometrics: SupervisorsAssessmentBiometrics | null;
    customControlData: any;
    id?: string;
    remarkBiometrics: string | null;
    remarkGrading: string | null;
}

interface ISupervisionItemEditState {
    errors: ValidationErrors;
    service?: IServiceGetOne;

    supervision?: ISupervisionEdit;
}

export class SupervisionItemEdit extends Component<ISupervisionItemEditProps, ISupervisionItemEditState> {
    constructor(props: ISupervisionItemEditProps) {
        super(props);

        this.state = {
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.loadService();

        const diagnosticReportCustomControlData = initializeCustomControl(
            this.props.process.customControlVersion,
            CustomControlEntity.DiagnosticReportAddEdit,
            this.props.process.code,
            this.props.process.serviceType,
            undefined,
            ReportCategories.examinationReport
        );
        const supervisionCustomControlData = initializeCustomControl(
            this.props.process.customControlVersion,
            CustomControlEntity.SupervisionEdit,
            this.props.process.code,
            this.props.process.serviceType
        );
        const customControlData = { ...supervisionCustomControlData, ...diagnosticReportCustomControlData };
        if (this.props.supervision.id) {
            await this.setState({
                supervision: {
                    ...this.props.supervision,
                    remarkGrading: this.props.supervision.remarkGrading || null,
                    remarkBiometrics: this.props.supervision.remarkBiometrics || null,
                    customControlData: this.props.supervision.customControlData || customControlData,
                    assessmentBiometrics: this.props.supervision.assessmentBiometrics || null,
                },
            });
        } else {
            await this.setState({
                supervision: {
                    id: this.props.supervision.id,
                    remarkGrading: null,
                    remarkBiometrics: null,
                    customControlData: customControlData,
                    assessmentBiometrics: null,
                },
            });
        }

        if (this.state.service?.episodeCategory) {
            startView(
                `/episode/${this.state.service.episodeCategory.name}/${this.state.service.code.toLowerCase()}_Supervision`
            );
        }
    }

    public render() {
        if (!this.state.service || !this.state.supervision) {
            return null;
        }

        const CustomControl = loadCustomControl(
            this.props.process.customControlVersion,
            CustomControlEntity.SupervisionEdit,
            this.state.service.code,
            this.state.service.serviceType
        );

        return (
            <CustomControl
                errors={this.state.errors}
                formId={this.props.formId}
                handleChange={async (name, value) => await this.handleChange(name, value)}
                handleChangeCustomControl={async (values) => await this.handleChangeCustomControl(values)}
                isFirstItem={this.props.isFirstItem}
                onCancel={this.props.onCancel}
                onSave={this.props.onSave}
                onSaveSupervision={async (draftOrFinal) => await this.saveSupervision(draftOrFinal)}
                process={this.props.process}
                service={this.state.service}
                supervision={this.state.supervision}
                validateOne={async (name) => await this.validateOne(name)}
                validateOneCustomControl={async (name) => await this.validateOneCustomControl(name)}
                validateSupervision={async (draftOrFinal) => await this.validateSupervision(draftOrFinal)}
            />
        );
    }

    private async handleChange<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ supervision }) => ({ supervision: { ...supervision, [name]: value } }));
    }

    private async validateOne(name: string) {
        if (!this.state.service) {
            return;
        }

        const validationType = getSupervisionValidationType(
            this.props.process.code,
            this.props.process.customControlVersion,
            'final'
        );
        await this.setState(({ supervision, errors }) => ({
            errors: updateValidationErrorsForOne(
                getSupervisionValidationSchema(validationType),
                supervision,
                errors,
                name
            ),
        }));
    }

    private async handleChangeCustomControl(values: any) {
        await this.setState(({ supervision }) => ({
            supervision: { ...supervision, customControlData: { ...supervision!.customControlData, ...values } },
        }));
    }

    private async validateOneCustomControl(name: string) {
        if (!this.state.service) {
            return;
        }
        const customControlName = 'customControlData.' + name;
        const validationType = getSupervisionValidationType(
            this.props.process.code,
            this.props.process.customControlVersion,
            'final'
        );
        await this.setState(({ supervision, errors }) => {
            errors = updateValidationErrorsForOne(
                getSupervisionValidationSchema(validationType),
                supervision,
                errors,
                customControlName
            );
            errors = validateOneCustomControlData(
                this.props.process.customControlVersion,
                CustomControlEntity.SupervisionEdit,
                this.state.service!.code,
                supervision!.customControlData,
                name,
                errors,
                this.props.process,
                undefined,
                this.state.supervision
            );

            return { errors };
        });
    }

    private async validateSupervision(draftOrFinal: 'draft' | 'final') {
        if (!this.state.service || !this.state.supervision) {
            return false;
        }
        const isDraft = draftOrFinal === 'draft';
        const validationType = getSupervisionValidationType(
            this.props.process.code,
            this.props.process.customControlVersion,
            draftOrFinal
        );
        const validationResult = validate(
            getSupervisionValidationSchema(validationType, isDraft),
            this.state.supervision
        );

        let errors = validationResult.errors;
        errors = validateCustomControlData(
            this.props.process.customControlVersion,
            CustomControlEntity.DiagnosticReportAddEdit,
            this.props.process.code,
            this.state.supervision.customControlData,
            errors,
            this.props.process,
            undefined,
            this.state.supervision
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, errors);

            return false;
        }

        return true;
    }

    private async saveSupervision(draftOrFinal: 'draft' | 'final') {
        const validationType = getSupervisionValidationType(
            this.props.process.code,
            this.props.process.customControlVersion,
            draftOrFinal
        );
        const isDraft = draftOrFinal === 'draft';
        const validatedSupervision = validate(getSaveAndSendValidationSchema(validationType, isDraft), {
            supervision: this.state.supervision,
            validationType,
        });

        if (validatedSupervision.errors.size > 0) {
            throw new Error(
                `Errors in save supervision validation:\n${validationErrorsToString(validatedSupervision.errors)}`
            );
        }

        if (draftOrFinal === 'final' && this.props.process.task) {
            const url = `/api/supervisions/saveAndSend/${this.props.process.task.id}`;
            const result = await fetchJson(url, 'POST', validatedSupervision.item);
            datadogRum.addAction(`Save ${kebabCase(this.state.service?.code.toLowerCase() + '_Supervision')} complete`);

            return result.json.id;
        } else if (this.props.process.task) {
            const url = `/api/supervisions/save/${this.props.process.task.id}`;
            const result = await fetchJson(url, 'POST', validatedSupervision.item);

            return result.json.id;
        }
    }

    private async loadService() {
        const url = `/api/services/${this.props.process.serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }
}
